<template>
  <v-list
    :disabled="disabled"
    rounded
  >

    <!-- QUANTITY -->
    <v-list-item v-if="! canEditMenu">
      <v-list-item-content>
        <v-list-item-title v-text="__('quantity')" />
      </v-list-item-content>
      <v-list-item-action>
        <v-row no-gutters align="center">
          <v-btn icon :disabled="decrementDisabled" @click="decrement()">
            <v-icon v-text="icons.minusCircle" />
          </v-btn>
          <v-btn icon @click="increment()">
            <v-icon v-text="icons.plusCircle" />
          </v-btn>
          <span
            class="black--text font-weight-bold ml-2"
            v-text="content.quantity"
          />
        </v-row>
      </v-list-item-action>
    </v-list-item>

    <!-- DISH ORDER -->
    <v-list-item
      v-if="showDishOrder"
      @click="$emit('goto', 'dishOrder')"
    >
      <v-list-item-title v-text="__('dishOrder')" />
      <v-list-item-subtitle class="text-right" v-text="dishOrderName" />
      <v-list-item-icon>
        <v-icon color="primary" v-text="icons.action" />
      </v-list-item-icon>
    </v-list-item>

    <!-- DISCOUNT -->
    <v-list-item
      v-if="can('applyDiscounts')"
      :disabled="discountDisabled"
      @click="$emit('goto', 'discount')"
    >
      <v-list-item-title v-text="__('discount')" />
      <v-list-item-subtitle class="text-right" v-text="discountText" />
      <v-list-item-icon>
        <v-icon :color="discountDisabled ? 'grey' : 'primary'" v-text="icons.action" />
      </v-list-item-icon>
    </v-list-item>

    <!-- MENU / MODIFIERS -->
    <v-list-item
      v-if="canEditMenu"
      @click="$emit('goto', 'menu')"
    >
      <v-list-item-title v-text="__('editMenu')" />
      <v-list-item-icon>
        <v-icon color="primary" v-text="icons.action" />
      </v-list-item-icon>
    </v-list-item>
    <v-list-item
      v-else-if="canEditModifiers"
      @click="$emit('goto', 'modifiers')"
    >
      <v-list-item-title v-text="__('editModifiers')" />
      <v-list-item-icon>
        <v-icon color="primary" v-text="icons.action" />
      </v-list-item-icon>
    </v-list-item>

    <!-- NOTES -->
    <v-textarea
      v-model="form.notes"
      :disabled="disabled"
      :label="__('notes')"
      solo
      flat
      rows="2"
      auto-grow
      clearable
      background-color="grey lighten-5"
      class="mx-1 pb-0"
      :hide-details="! form.notes || form.notes.length <= 255"
      :counter="form.notes && form.notes.length > 255 ? 255 : false"
      @input="$emit('input', {
        field: 'notes',
        value: $event,
      })"
    ></v-textarea>

    <!--
      CANCEL
      Note that we use a button instead of the whole list-item to reduce the
      sensitive size.
    -->
    <v-list-item :disabled="cannotBeRemoved">
      <v-list-item-title v-text="__('delete')" />
      <v-list-item-action>
        <v-btn
          icon
          :disabled="cannotBeRemoved"
          @click="remove()"
        >
          <v-icon v-text="icons.delete" />
        </v-btn>
      </v-list-item-action>
    </v-list-item>

    <!-- TO-DO... -->
    <!-- TO-DO... -->
    <!-- TO-DO... -->
    <!--ul>
      <li>Preu (TO-DO es pot modificar) {{ content.itemPrice | currency }}</li>
    </ul-->
    <!--ul>
      <li>Modificadors opcionals</li>
      <li>Separar</li>
      <li>Ja imprès</li>
    </ul-->
  </v-list>
</template>

<script>
  import I18n from '@/Mixins/I18n'
  import Lockable from '@/Mixins/Lockable'
  import NeedsPermission from '@/Mixins/NeedsPermission'
  import UsesIcons from '@/Mixins/UsesIcons'
  import FranceLog from '@/Models/Static/FiscalCountry/FranceLog'

  export default {
    mixins: [
      I18n,
      Lockable,
      NeedsPermission,
      UsesIcons,
    ],

    props: {
      content: {
        required: true,
        type: Object,
      },
      form: {
        required: true,
        type: Object,
      },
      canEditMenu: {
        required: true,
        type: Boolean,
      },
      canEditModifiers: {
        required: true,
        type: Boolean,
      },
    },

    data: () => ({
      FranceLog,
    }),

    computed: {
      disabled () {
        return this.$store.state.orders.dialogs.locked
      },
      order () {
        return this.$store.state.orders.current
      },
      minQuantity () {
        return Math.max(1, this.content.alreadyPaidQuantity)
      },
      decrementDisabled () {
        return this.content.quantity <= this.minQuantity || this.order.invalidMinQuantity(this.content.item, -1)
      },
      useDishOrder () {
        return !! this.$store.getters['orders/config']('shouldOrderDishes')
      },
      dishOrders () {
        return this.$store.getters['catalog/activeDishOrders']
      },
      showDishOrder () {
        return this.useDishOrder && this.dishOrders.length > 1
      },
      dishOrderName () {
        return this.$store.getters['catalog/dishOrderByOrder'](this.content.dishOrder)?.name
      },
      discountDisabled () {
        return this.content.alreadyPaidQuantity > 0
      },
      discountText () {
        const d = this.form.discount
        if (! d || typeof d !== 'object' || Object.keys(d).length === 0) {
          return ''
        }
        return !! d.isPercentage
          ? `${d.discount}%`
          : Number(d.discount).toFixed(2)
      },
      cannotBeRemoved () {
        return ! this.content.canBeRemoved
      },
    },

    methods: {
      increment () {
        if (this.locked) {
          return
        }
        this.lock()
        this.$store.commit('orders/incrementContent', this.content.index)
      },
      decrement () {
        if (this.locked) {
          return
        }
        this.lock()
        this.$store.commit('orders/decrementContent', this.content.index)
      },
      remove () {
        this.$logFrance(this.FranceLog.ABANDONMENT_OF_A_NON_VALIDATED_SALE_TRANSACTION, `Removed ${this.content.quantity} x ${this.content.itemName} (ID ${this.content.item_id})`)
        this.$store.commit('orders/removeContent', this.content.index)
        this.$emit('close')
      },
    },
  }
</script>
